import { Component, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { Contact } from "@models/common/contact";
import { RelationshipType } from "@models/relationships/relationship-type.enum";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonModalFormComponent } from "@shared/components/commons/common-modal-form/common-modal-form.component";
import { DividerComponent } from "@shared/components/commons/divider/divider.component";
import { CheckboxComponent } from "@shared/controls/checkbox/checkbox.component";
import { InputNumberComponent } from "@shared/controls/input-number/input-number.component";
import { InputComponent } from "@shared/controls/input/input.component";
import {
  BsbMaskedInputComponent
} from "@shared/controls/masked-input/specific-masked-inputs/bsb-masked-input/bsb-masked-input.component";
import { ContactsApiService } from "@shared/services/contacts-api.service";
import { setControlDisabled } from "@shared/utils/set-control-disabled";
import { ToastrService } from "ngx-toastr";
import { ContactsSelectComponent } from "../../../../../../forms/controls/contacts-select/contacts-select.component";
import { AutoPayAgreementDto } from "../../auto-pay.component";

@Component({
  selector: 'app-auto-pay-agreement-modal',
  standalone: true,
  imports: [
    CommonModalFormComponent,
    DividerComponent,
    CheckboxComponent,
    ReactiveFormsModule,
    BsbMaskedInputComponent,
    InputComponent,
    ContactsSelectComponent,
    InputNumberComponent
  ],
  templateUrl: 'auto-pay-agreement-modal.component.html',
  styleUrl: 'auto-pay-agreement-modal.component.scss'
})
export class AutoPayAgreementModalComponent implements OnInit {
  activeModal = inject(NgbActiveModal);
  contactsApiService = inject(ContactsApiService);
  destroyRef = inject(DestroyRef);
  toastr = inject(ToastrService);

  @Input() entityId!: string;
  @Input() editData: AutoPayAgreementDto | null = null;

  isLoading = signal(false);
  contacts: Contact[] = [];

  form = new FormGroup({
    haveBankAccount: new FormControl<boolean>(false),
    bsb: new FormControl<string | null>(null, [Validators.required]),
    bankAccountDetails: new FormControl<string | null>(null, [Validators.required]),
    accountName: new FormControl<string | null>(null, [Validators.required]),
    contactId: new FormControl<string | null>(null, [Validators.required]),
  });

  ngOnInit(): void {
    this.loadContacts();

    this.listenHaveBankAccountChanges();
    this.form.controls.haveBankAccount.setValue(true);

    // demo purpose only
    setControlDisabled(this.form.controls.haveBankAccount);
  }

  setupChange(): void {
    if (this.editData && this.entityId == this.editData.entityId) {
      this.form.patchValue({ ...this.editData });
    }
  }

  submit(): void {
    if (this.form.invalid) {
      this.toastr.error('Please fill required fields', 'Error');
      this.form.markAllAsTouched();
      return;
    }

    const payload = new AutoPayAgreementDto({
      ...this.form.value as Partial<AutoPayAgreementDto>,
      entityId: this.entityId,
    });

    this.activeModal.close(payload);
  }

  loadContacts() {
    if (!this.entityId) throw new Error('No entity id passed');

    this.isLoading.set(true);
    this.contactsApiService.getContacts(this.entityId, [RelationshipType.Director])
      .subscribe({
        next: (contacts) => {
          this.contacts = contacts;
          this.setupChange();
          this.isLoading.set(false);
        },
        error: (error) => {
          console.log('[AutoPayAgreementModalComponent.loadContacts] error: ', error);
          this.isLoading.set(false);
        }
      });
  }

  close(): void {
    this.activeModal.dismiss();
  }

  private listenHaveBankAccountChanges(): void {
    this.form.controls.haveBankAccount.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((haveBankAccount) => {
        setControlDisabled(this.form.controls.bsb, !haveBankAccount);
        setControlDisabled(this.form.controls.bankAccountDetails, !haveBankAccount);
        setControlDisabled(this.form.controls.accountName, !haveBankAccount);
      });
  }
}
